import {useEffect, useMemo, useRef, useState} from 'react';

import {switchCustomerAndReload} from '~utils/customerSwitch';
import {orgIdQueryParam, useQueryParam} from '~utils/routeUtil';
import {selectAvailableCustomers, selectCustomer} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

/**
 * Handles the `orgID` deep-link query param. When the app is opened via a link
 * whose org differs from the currently logged-in customer, and the user has
 * access to that org, this automatically switches the customer and reloads onto
 * the intended page — instead of bouncing the user to the invalid-project page.
 *
 * The `orgID` param is intentionally never stripped from the URL so the link
 * stays shareable/scoped to the specific org. To keep it meaningful, this hook
 * also normalizes the param: a missing `orgID` is backfilled with the current
 * customer, and an inaccessible/nonexistent `orgID` (not in the user's available
 * customers) is corrected back to the current session's customer.
 *
 * @returns
 * - `needsCustomerSwitch`: an org switch is required and possible; callers should
 *   suppress project-validation redirects while this is true.
 * - `isSwitchingCustomer`: the switch API call is in flight (a full reload follows).
 */
export function useOrgIdFromUrl(): {needsCustomerSwitch: boolean; isSwitchingCustomer: boolean} {
  const customer = useAppSelector(selectCustomer);
  const availableCustomers = useAppSelector(selectAvailableCustomers);
  const [isSwitchingCustomer, setIsSwitchingCustomer] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  const hasAttemptedSwitch = useRef(false);

  const [orgIdParam, setOrgIdParam] = useQueryParam(orgIdQueryParam);
  const orgId = typeof orgIdParam === 'string' ? orgIdParam : undefined;

  const [isTabVisible, setIsTabVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    // A failed switch is almost always transient (canSwitch already verified
    // org access), so allow one retry each time the user returns to the tab.
    // Bounded: one attempt per return, and a reload only happens on success.
    const retryFailedSwitch = () => {
      setSwitchFailed((failed) => {
        if (failed) {
          hasAttemptedSwitch.current = false;
        }
        return false;
      });
    };
    const handleFocus = () => retryFailedSwitch();
    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsTabVisible(visible);
      if (visible) {
        retryFailedSwitch();
      }
    };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const canSwitch = useMemo(() => {
    // Wait until the user data (customer + available customers) is loaded.
    if (!orgId || !customer || availableCustomers.length === 0) {
      return false;
    }
    // Already on the right org: nothing to do (keep the param for shareability).
    if (orgId === customer) {
      return false;
    }
    // Only switch to orgs the user actually has access to; otherwise fall through
    // to the normal invalid-project flow.
    return availableCustomers.includes(orgId);
  }, [orgId, customer, availableCustomers]);

  // While a switch attempt has failed, let the invalid-project fallback take
  // over; the failure is retried the next time the user returns to the tab.
  const needsCustomerSwitch = canSwitch && !switchFailed;

  useEffect(() => {
    if (!needsCustomerSwitch || !isTabVisible || hasAttemptedSwitch.current || !orgId) {
      return;
    }
    hasAttemptedSwitch.current = true;
    setIsSwitchingCustomer(true);

    // Preserve the current URL (path + query + hash) so the user lands on the
    // page they deep-linked into, now under the correct org.
    const targetUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    switchCustomerAndReload(orgId, targetUrl).catch((error) => {
      console.error('Failed to switch customer from orgID URL param:', error);
      setIsSwitchingCustomer(false);
      setSwitchFailed(true);
    });
  }, [needsCustomerSwitch, isTabVisible, orgId]);

  // Keep the `orgID` param in the URL scoped to an org the user actually has.
  useEffect(() => {
    if (!customer) {
      return; // Wait until the user data is loaded.
    }
    // Backfill a missing orgID so the shareable URL always carries the current org.
    if (!orgId) {
      setOrgIdParam(customer);
      return;
    }
    // Already scoped to the current org.
    if (orgId === customer) {
      return;
    }
    // Can't judge accessibility until the available customers list has loaded.
    if (availableCustomers.length === 0) {
      return;
    }
    // Accessible org → the switch effect above handles it (switch + reload); keep the param.
    if (availableCustomers.includes(orgId)) {
      return;
    }
    // Inaccessible / nonexistent org → correct the URL back to the session's org.
    setOrgIdParam(customer);
  }, [orgId, customer, availableCustomers, setOrgIdParam]);

  return {needsCustomerSwitch, isSwitchingCustomer};
}
